import type { FC } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Box, Image } from '@chakra-ui/react'
import {
	Banner,
	BannerAvatar,
	BannerHeading,
	BannerSubHeading,
	BannerBackgroundCircle,
	BannerImageText,
	BannerSimpleImage,
	BannerButton,
	BannerImageTextWithStarIcon,
} from '@alexanderathoodly/ui-library'
import type { Expert } from '~/forms/utils'
import { GAAction, sendGA4Event } from '~/analytics/ga'
import OneToTenWsImg from '#/contentpage/1to10ws_for_banner.png'
import FifteenPlusWsImg from '#/contentpage/15plusws_for_banner.png'
import { COWORKING_TYPE, SERVICED_OFFICE_TYPE } from './urlGenerator'
import { useTranslation } from 'react-i18next'
import { useSerpPageTypeInSwedish } from './utils'

export const useTo = (): string => {
	const { t } = useTranslation()
	const { pathname } = useLocation()
	const page = pathname.split('/')[1]
	if (page === COWORKING_TYPE) {
		return t('/coworkinghjalpen', { ns: 'url' })
	}
	if (page === SERVICED_OFFICE_TYPE) {
		return t('/kontorshotellshjalpen', { ns: 'url' })
	}
	return t('/kontorshjalpen', { ns: 'url' })
}

export const ResultBannerWithExpert: FC<{
	todaysExpert: Expert
}> = ({ todaysExpert }) => {
	const { t } = useTranslation()
	const pageType = useSerpPageTypeInSwedish()
	return (
		<Box as={Link} to={useTo()}>
			<Banner
				variant="serpExpert"
				size="xl"
				h={{ base: 'auto', md: '215px' }}
				mb={5}
				py={0}
				px={0}
				pl={{ base: 5, md: 10 }}
				onClick={() => {
					GAAction({
						category: 'engagement',
						action: 'click_expert_banner',
						label: 'expert',
					})
					sendGA4Event('click_kontorshjalpen', {
						page_type: 'serp',
						placement: 'content',
						placement_type: 'banner',
						content: 'expert',
					})
				}}
			>
				<BannerAvatar>
					<Image
						src={todaysExpert.img}
						borderRadius="full"
						w={{ base: '80px', md: '100px' }}
						h={{ base: '80px', md: '100px' }}
						alt={todaysExpert.name}
					/>
				</BannerAvatar>
				<BannerBackgroundCircle />
				<BannerImageText mt={2}>
					{todaysExpert.name}, {t('Kontorsexpert')}
				</BannerImageText>
				<BannerHeading>
					{t(`Jämför hela flexibla kontorsmarknaden - ${pageType}`)}
				</BannerHeading>
				<BannerSubHeading>
					{t(
						`Vårt team av experter tar fram förslag som passar ert team - kostnadsfritt - ${pageType}`
					)}
				</BannerSubHeading>
				<BannerButton>{t('Bli matchad')}</BannerButton>
			</Banner>
		</Box>
	)
}

const BANNER_TEXT = {
	serpRightImage: {
		imgSrc: OneToTenWsImg,
		imgAlt: 'kontor-för-en-till-tio',
		imgW: { base: '100px', md: '110px', lg: '120px' },
		imgH: { base: '100px', md: '110px', lg: '120px' },
		imgText: 'Verifierade kontorsförslag med pris och villkor',
		imgTextM: { ml: 'auto', mr: 5 },
		heading: 'Få skräddarsydda förslag för ert team',
		subHeading:
			'Fyll i era behov och få en shortlist skräddarsydd efter era behov - kostnadsfritt',
		pl: { base: 5, md: 10 },
		pr: 0,
		GALabel: '1_to_10',
	},
	serpLeftImage: {
		imgSrc: FifteenPlusWsImg,
		imgAlt: 'kontor-för-femton-plus',
		imgW: { base: '90px', md: '130px', lg: '190px' },
		imgH: { base: '55px', md: '80px', lg: '120px' },
		imgText: 'Väx med en egen avdelning skapad för större team',
		imgTextM: { ml: 5, mr: 'auto' },
		heading: 'Är ni ett större team? Låt oss hjälpa er',
		subHeading:
			'Vi tar kostnadsfritt fram förslag på egna avdelningar med flexibla avtalsvillkor',
		pl: 0,
		pr: { base: 5, md: 10 },
		GALabel: 'large_team',
	},
}

interface ResultBannerWithOfficeImageProps {
	variant: 'serpLeftImage' | 'serpRightImage'
}

export const ResultBannerWithOfficeImage: FC<
	ResultBannerWithOfficeImageProps
> = ({ variant }) => {
	const { t } = useTranslation()
	const pageType = useSerpPageTypeInSwedish()
	const {
		imgSrc,
		imgAlt,
		imgW,
		imgH,
		imgText,
		imgTextM,
		heading,
		subHeading,
		pl,
		pr,
		GALabel,
	} = BANNER_TEXT[variant]
	return (
		<Box as={Link} to={useTo()} px={0}>
			<Banner
				variant={variant}
				maxH="215px"
				height={{ base: 'min-content', md: '215px' }}
				size="xl"
				mb={5}
				py={0}
				px={0}
				pl={pl}
				pr={pr}
				onClick={() => {
					GAAction({
						category: 'engagement',
						action: 'click_expert_banner', // keep this action to use the same grouping for the all banners
						label: GALabel,
					})
					sendGA4Event('click_kontorshjalpen', {
						page_type: 'serp',
						placement: 'content',
						placement_type: 'banner',
						content: GALabel,
					})
				}}
			>
				<BannerSimpleImage>
					<Image src={imgSrc} w={imgW} h={imgH} alt={imgAlt} />
				</BannerSimpleImage>
				<BannerBackgroundCircle />
				<BannerImageTextWithStarIcon mt={6} ml={imgTextM.ml} mr={imgTextM.mr}>
					{t(`${imgText} - ${pageType}`)}
				</BannerImageTextWithStarIcon>
				<BannerHeading>{t(`${heading} - ${pageType}`)}</BannerHeading>
				<BannerSubHeading>{t(`${subHeading} - ${pageType}`)}</BannerSubHeading>
				<BannerButton>{t('Bli matchad')}</BannerButton>
			</Banner>
		</Box>
	)
}
